import { FormattedMessage } from 'react-intl';
import { getLink } from '../../../services/link-service';
import { linkIDs } from '../../../services/link-service/links';
import { ERROR_CATEGORIES } from './error-categories';
import { messages } from './messages-v2';

const errorLookup = {
  ReturnErrorMessageFromEntityLog: 'ReturnErrorMessageFromEntityLog',
  EbayReconfigureYourConnection: {
    message: 'ebayReconfigureYourConnectionError',
    link: linkIDs.ReconfigureEbay,
  },
  EtsyUnAuthorized: {
    message: 'etsyUnauthorizedError',
    link: linkIDs.ReconfigureEtsy,
  },
  EtsyReconfigureYourConnection: {
    message: 'etsyReconfigureYourConnectionError',
    link: linkIDs.ReconfigureEtsy,
  },
  AmazonReconfigureYourConnection: {
    message: 'amazonReconfigureYourConnectioneError',
    link: linkIDs.ReconfigureStore,
  },
  AllStoresShipStationStatus: {
    message: 'allStoresShipStationStatusError',
    link: linkIDs.ShipStationStatus,
  },
  AllStoresForbidden: {
    message: 'allStoresForbiddenError',
    link: linkIDs.StoreErrorHelp,
  },
  AllStoresReconfigureYourConnection: {
    message: 'allStoresReconfigureYourConnectionError',
    link: linkIDs.ReconfigureStore,
  },
  AllStoresSystemCapacity: {
    message: 'allStoresSystemCapacityError',
    link: linkIDs.ReconfigureStore,
  },
};

export const extractHttpStatusCode = (errorMessage) => {
  if (!errorMessage || typeof errorMessage !== 'string') return null;

  // Look for 3-digit number starting with 4 or 5 followed by a semicolon
  // Example: "520 :" or "403:"
  const match = errorMessage.match(/^([4-5]\d{2})\s*:/);

  return match ? match[1] : null;
};

/**
 * Determine error category based on status code and keywords
 * @param {string} errorMessage - The full error message
 * @returns {object} - Error category object with code, name, description
 */
export const determineErrorCategory = (errorMessage) => {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return ERROR_CATEGORIES.UNKNOWN;
  }

  const lowerMessage = errorMessage.toLowerCase();
  const statusCode = extractHttpStatusCode(errorMessage);

  // First try to match by status code
  if (statusCode) {
    const categoryByCode = Object.values(ERROR_CATEGORIES).find((category) => category.code === statusCode);
    if (categoryByCode) {
      return categoryByCode;
    }
  }

  // Then try to match by keywords
  const categoryByKeywords = Object.values(ERROR_CATEGORIES).find((category) =>
    category.keywords.some((keyword) => lowerMessage.includes(keyword)),
  );

  return categoryByKeywords || ERROR_CATEGORIES.UNKNOWN;
};

export const parseErrorForDisplay = (errorMessage) => {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return {
      errorCode: null,
      headerMessage: 'Unknown Error',
      errorContents: 'No error details available',
      category: ERROR_CATEGORIES.UNKNOWN,
    };
  }

  const category = determineErrorCategory(errorMessage);
  const statusCode = extractHttpStatusCode(errorMessage);

  return {
    errorCode: category.code || statusCode,
    headerMessage: category.name,
    errorContents: errorMessage, // Raw message
    category,
  };
};

export const getStatusCodeDescription = (statusCode) => {
  const category = Object.values(ERROR_CATEGORIES).find((cat) => cat.code === statusCode);
  return category ? category.name : 'Error';
};

export const extractUserFriendlyMessage = (errorMessage) => {
  const parsed = parseErrorForDisplay(errorMessage);
  return parsed.errorContents;
};

export const parseErrorMessageForUser = (errorObject, homeCountry, locale) => {
  const { storeRefreshErrorCode, error } = errorObject;
  const userFriendlyError = errorLookup[storeRefreshErrorCode] || 'ReturnErrorMessageFromEntityLog';
  if (userFriendlyError === 'ReturnErrorMessageFromEntityLog' || locale !== 'en' || homeCountry !== 'US') {
    return error;
  }

  return (
    <FormattedMessage
      {...messages[userFriendlyError.message]}
      values={{
        a: (chunks) => (
          <a href={getLink(userFriendlyError.link, homeCountry, locale)} target='blank'>
            {chunks}
          </a>
        ),
      }}
    />
  );
};
